import React from 'react';
import DeleteButton from '@/components/common/DeleteButton';
import EditButton from '@/components/common/EditButton';
import usePreselectedLabels from '@/lib/hooks/swr/usePreselectedLabels';
import translate from '@/lib/intl/translate';
import type Label from '@/types/Label';
import { formatCurrency } from '@/utils/currency';
import AutomationType from '../constants/AutomationType';
// eslint-disable-next-line import/no-cycle -- TODO: Fix this the next time the file is edited.
import { useAutomationsContext } from './AutomationsProvider';

function AutomationsListItem({
  automation,
}: {
  automation: MajorGiftAutomation;
}) {
  const { editAutomation, deleteAutomation } = useAutomationsContext();
  const { automationId, maxDonation, minDonation, type } = automation;

  const { data: labelData } = usePreselectedLabels(automation?.labelIds?.tags);

  const title = React.useMemo(() => {
    let translationKey;
    if (typeof minDonation === 'number') {
      translationKey = 'AUTOMATIONS_DESCRIPTION_MIN_DONATION';
    }
    if (typeof maxDonation === 'number') {
      translationKey = 'AUTOMATIONS_DESCRIPTION_MAX_DONATION';
    }
    if (typeof minDonation === 'number' && typeof maxDonation === 'number') {
      translationKey = 'AUTOMATIONS_DESCRIPTION_RANGE';
    }
    return translationKey
      ? translate(translationKey, {
          taskTitle:
            type === AutomationType.TASK
              ? automation.taskTitle
              : translate('AUTOMATIONS_CREATE_LABEL'),
          minDonation: formatCurrency(minDonation, true),
          maxDonation: formatCurrency(maxDonation, true),
        })
      : automation.taskTitle;
  }, [automation]);

  const subTitle = React.useMemo(() => {
    if (type === AutomationType.TASK && automation.taskAssignee) {
      return translate('AUTOMATIONS_ASSIGNED_TO', {
        displayName: automation.taskAssignee.displayName,
      });
    }
    if (type === AutomationType.LABEL && labelData) {
      return (labelData as Label[])
        .map(({ labelName }) => labelName)
        .join(', ');
    }
    return null;
  }, [automation, labelData]);

  return (
    <li>
      <div className="flex items-center gap-4">
        <div className="line-clamp-2">{title}</div>
        <div className="ml-auto flex gap-4">
          <EditButton
            className="text-text-secondary"
            iconClassName="text-base"
            onClick={() => editAutomation(automation)}
          />
          <DeleteButton
            className="text-text-secondary"
            iconClassName="text-base"
            onClick={() => deleteAutomation(automationId)}
          />
        </div>
      </div>
      <div className="text-text-secondary">{subTitle}</div>
    </li>
  );
}

export default AutomationsListItem;
